import React, { memo, useMemo, useState } from "react";
import clsx from "clsx";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import WheelItem from "./WheelItem";
import WinterWheelItem from "./WinterWheelItem";
import CarDataConsolidator from "../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import styles from "./Wheels.module.scss";
import toggle from "../../../../../../shared-components/css-modules/toyota/organisms/Switch.module.scss";
import { trackOptionsTabSwitch } from "../../../../../utils/tracking";
import WinterWheelFitting from "./WinterWheelFitting";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import type { SectionWrapperPropagationProps } from "../SectionWrapper";
import SectionWrapper from "../SectionWrapper";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import WheelSkeleton from "./WheelSkeleton";
import { toggleWinterWheelTab } from "../../../../../redux/slices/CarConfigNavigationSlice";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import SliderWrapper from "../../../common/spin/sliderWrapper/SliderWrapper";

const Wheels: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }) => {
    const dispatch = useCarConfigDispatch();
    const wheels = useCarConfigSelector(
        (state) => CarDataConsolidator.getWheels(state.items.wheels, state.basket, state.items.packs),
        shallowEqual,
    );
    const wheelsInOtherGrades = useCarConfigSelector((state) => state.availableInOtherGrades.availableWheels);
    const winterWheels = useCarConfigSelector(
        (state) =>
            CarDataConsolidator.getWinterWheels(
                state.items.winterWheels.filter((wheel) => wheel.valid),
                state.basket.colour,
                state.basket.upholstery,
            ),
        shallowEqual,
    );
    const winterWheel = useCarConfigSelector((state) => state.basket.winterWheel);
    const itemsAreInitialized = useCarConfigSelector((state) => state.items.initialized);

    const carConfigBuyNow = useCarConfigLabel("carConfigAvailableInOtherGradesSummaryTitle");
    const winterWheelsActive = useCarConfigSelector((state) => state.carConfigNavigation.winterWheelTabActive);

    // Having local state so the WinterWheelFitting section keeps working when the installation gets removed
    // Need to define it here considering when selecting a winter wheel, it also needs to know about the installation (or the last value of the WinterWheelFitting installation checkbox)
    const [includeInstallation, setIncludeInstallation] = useState(true);

    // It's possible that the installation gets removed (ex.: in the overview) hence we prioritize the winterwheel check over the local state
    const installTheWheels = winterWheel ? winterWheel.installTheWheels : includeInstallation;

    const [ariaToggleSwitch, ariaWinterWheelsPanel] = useCarConfigLabel([
        "carConfigA11yButtonToggleSwitch",
        "carConfigA11yPanelWinterWheels",
    ]);

    return useMemo(
        () => (
            <SectionWrapper
                sectionId={CarConfigConfigureSection.Wheels}
                sectionRef={sectionRefs.get(CarConfigConfigureSection.Wheels)}
                hasToggle={winterWheels.length > 0}
            >
                <header>
                    <Text tag="h2" variant="apheleia-h2" spacing="spacing-4">
                        <CarConfigLabel label="carConfigWheels" />
                    </Text>
                    <SliderWrapper sliderFrames={[27]} iswheel />
                    {winterWheels.length > 0 && (
                        <div className={clsx(styles.togglewrap, styles["has-panels"])}>
                            <div>
                                <nav className={toggle.switch} aria-label={ariaToggleSwitch}>
                                    <input
                                        id="winter-wheels-toggle-on"
                                        className={`${toggle.toggle} ${toggle.left}`}
                                        name="toggleWinterWheels"
                                        value="false"
                                        type="radio"
                                        onChange={() => {
                                            trackOptionsTabSwitch("wheels");
                                            dispatch(toggleWinterWheelTab());
                                        }}
                                        defaultChecked={!winterWheelsActive}
                                    />
                                    <label htmlFor="winter-wheels-toggle-on" className={toggle.option}>
                                        <CarConfigLabel label="carConfigWheelsToggle" />
                                    </label>
                                    <input
                                        id="winter-wheels-toggle-off"
                                        className={`${toggle.toggle} ${toggle.right}`}
                                        name="toggleWinterWheels"
                                        value="true"
                                        type="radio"
                                        onChange={() => {
                                            trackOptionsTabSwitch("winterwheels");
                                            dispatch(toggleWinterWheelTab());
                                        }}
                                        defaultChecked={winterWheelsActive}
                                    />
                                    <label htmlFor="winter-wheels-toggle-off" className={toggle.option}>
                                        <CarConfigLabel label="carConfigWinterWheelsToggle" />
                                    </label>
                                </nav>
                            </div>
                        </div>
                    )}
                </header>
                <div className={winterWheels.length > 0 ? styles.panels : ""}>
                    <div className={`${toggle.panel} ${!winterWheelsActive ? toggle.active : toggle.inactive}`}>
                        <div className={styles.wheels}>
                            <WheelSkeleton loading={!itemsAreInitialized}>
                                {wheels.map((wheel, index) => (
                                    <WheelItem
                                        key={wheel.id}
                                        wheel={wheel}
                                        index={index}
                                        onlyChild={wheels.length === 1}
                                    />
                                ))}
                            </WheelSkeleton>
                        </div>
                        {wheelsInOtherGrades.length > 0 && (
                            <Collapse summary={carConfigBuyNow} className={styles.other}>
                                {/* Disable because this is only a prevention and not an actual click for the user*/}
                                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,jsx-a11y/no-noninteractive-element-interactions */}
                                <nav
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    className={styles.wheels}
                                    aria-label={ariaToggleSwitch}
                                >
                                    {wheelsInOtherGrades.map((wheel, index) => (
                                        <WheelItem key={wheel.id} wheel={wheel} index={index} wheelOtherGrade />
                                    ))}
                                </nav>
                            </Collapse>
                        )}
                    </div>
                    <div className={`${toggle.panel} ${winterWheelsActive ? toggle.active : toggle.inactive}`}>
                        <WinterWheelFitting
                            winterWheel={winterWheel}
                            setIncludeInstallation={setIncludeInstallation}
                            installTheWheels={installTheWheels}
                        />

                        <nav className={styles["winter-wheels"]} aria-label={ariaWinterWheelsPanel}>
                            {winterWheels.map((wheel, index) => (
                                <WinterWheelItem
                                    key={wheel.id}
                                    wheel={wheel}
                                    index={index}
                                    installTheWheels={installTheWheels}
                                />
                            ))}
                        </nav>
                    </div>
                </div>
            </SectionWrapper>
        ),
        [
            dispatch,
            installTheWheels,
            itemsAreInitialized,
            sectionRefs,
            wheels,
            wheelsInOtherGrades,
            winterWheel,
            winterWheels,
            winterWheelsActive,
        ],
    );
};

export default memo(Wheels);
